/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {isGGCInitializedSelector} from '../store/selectors/global-group';
import {LocalizedPageProps, PageReadyDependencyProps} from '../types/pages';

/**
 * The usePageReady hook is used to determine whether a page is ready to be
 * rendered based on the initialization status of the Global Group Chooser (GGC)
 * and the translation readiness (tReady). It's particularly useful in scenarios
 * where you want to ensure that a page is only rendered when both the GGC has been
 * initialized and translations are ready.
 *
 * ```typescript
 *
 *  import {usePageReady} from '@ion/applications/src/hooks/usePageReady';
 *
 *  export const Component = ({tReady}) => {
 *    const {isReady} = usePageReady({tReady});
 *
 *    if (isReady) {
 *      return <div>Your Page Content</div>;
 *    }
 *    return <div>Loading...</div>;
 *  };
 *
 * ```
 *
 * @param tReady: A boolean indicating whether translations are ready for use.
 * @returns An object containing the following property:
 * isReady: A boolean value indicating whether the page is ready for rendering.
 * It's true when both the GGC is initialized and translations are ready; otherwise, it's false.
 */
export const usePageReady = ({tReady}: LocalizedPageProps): PageReadyDependencyProps => {
  const isGGCInitialized = useSelector(isGGCInitializedSelector);

  const isReady = useMemo(() => (
    isGGCInitialized && tReady
  ), [isGGCInitialized, tReady]);

  return {isReady};
};

export default usePageReady;
